import { RiEyeOffLine, RiFileCopyLine } from "@remixicon/react";
import type {
  ApproachMetricsView,
  ApproachOutcomeView,
  ClosedApproachView,
  DensityContextView,
  DensityDetail,
  DensitySnapshot,
  LibraryRecordView,
  OngoingApproachView,
  WrappedCoin,
} from "@screener/protocol";
import { Button } from "@screener/uikit/components/button";
import { LoadingRegion, SkeletonLines } from "@screener/uikit/components/loading-skeleton";
import { LockedBanner } from "@screener/uikit/components/locked-banner";
import { LockedInline } from "@screener/uikit/components/locked-inline";
import { SectionLabel } from "@screener/uikit/components/section-label";
import { Skeleton } from "@screener/uikit/components/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@screener/uikit/components/tooltip";
import { cn } from "@screener/uikit/lib/utils";
import { memo, type ReactNode, useRef } from "react";
import { formatUsd, LIBRARY_HISTORY_LABELS, stateLabel, tickerLabel, venueOf } from "@/lib/labels";
import { usePlanGate } from "@/lib/plan-gate-context";
import { TerminalDetailAction, TerminalDetailSettings } from "@/terminal/terminal-button";
import { type ActionNotice, noticeForDensity } from "./action-notice";
import { tickerForClipboard } from "./copy-ticker";
import { Ago, Elapsed } from "./elapsed";
import { type FavoriteToggleNotice, favoriteToggleNoticeText } from "./favorite-notice";
import { type FavoriteIndicator, FavoriteStarButton } from "./favorite-star-button";
import {
  formatAge,
  formatClock,
  formatDateTime,
  formatDistance,
  formatFrontrunNearest,
  formatOptionalAge,
  formatOptionalPercent,
  formatOptionalUsd,
  formatPercent,
  formatPrice,
  formatQty,
} from "./format";
import { type HideCoinNotice, hideCoinNoticeText } from "./hidden-coin-notice";
import type { NoteWriteResult } from "./notes-api";
import { NotesSection } from "./notes-section";
import { type RowPinMatch, rowPinState } from "./pin-match";
import { PinToggleButton } from "./pin-toggle-button";
import { MmFlagBadges, SideBadge } from "./side-badge";
import type { NotesState } from "./use-notes";
import { WrappedMark } from "./wrapped-mark";

/**
 * Состояние запроса деталей §7.6 глазами панели. Отказы различаются, а не
 * сводятся к одной ошибке: «плотности уже нет» означает, что смотреть больше
 * нечего, а «демон недоступен» — что повторить осмысленно.
 */
export type DetailState =
  | { status: "loading" }
  | { status: "ready"; detail: DensityDetail }
  | { status: "not-found" }
  | { status: "unavailable" };

interface DensityDetailContentProps {
  /** Перечень обёрнутых монет из приветствия — по нему заголовок ставит пометку у пары. */
  wrapped: readonly WrappedCoin[];
  /**
   * Плотность из потока: заголовок панели стоит с первого кадра, до ответа
   * маршрута, и продолжает стоять, когда ответ не пришёл вовсе.
   */
  row: DensitySnapshot;
  state: DetailState;
  onRefresh: () => void;
  /** Заметки §6.4 монеты этой плотности — общий список, не запрос по деталям. */
  notesState: NotesState;
  onCreateNote: (base: string, text: string) => Promise<NoteWriteResult>;
  onUpdateNoteText: (id: string, text: string) => Promise<NoteWriteResult>;
  onDeleteNote: (id: string) => Promise<"ok" | "not-found" | "unavailable">;
  /**
   * Монеты, отмеченные избранным целиком §6.6 — то же сопоставление, что у
   * строки таблицы (web-density-detail «Панель несёт звёздочку избранного
   * монеты»). `null` — список ещё не прочитан или недоступен.
   */
  favoriteBases: Set<string> | null;
  onToggleFavorite: (row: DensitySnapshot) => void;
  pendingFavoriteBases: ReadonlySet<string>;
  /**
   * Отказ последней записи избранного — назван здесь, панель не закрывается.
   * Несёт монету (review stage6-favorites, п. 5): панель показывает его,
   * только если он о её собственной монете, — последнее нажатие где-то ещё
   * в таблице панель не касается.
   */
  favoriteNotice: FavoriteToggleNotice;
  /**
   * Скрыть монету этой плотности целиком (§75 п. 2) — кнопка «Скрыть монету».
   * Закрытие панели при успехе — дело экрана: плотность уходит из состава.
   */
  onHideCoin: (base: string) => void;
  /**
   * Отказ последнего скрытия — назван в панели, она остаётся открытой. Несёт
   * монету, как `favoriteNotice`: панель показывает его, только если он о её
   * собственной монете.
   */
  hideNotice?: HideCoinNotice;
  /**
   * Исход копирования тикера или отправки в терминал — то же состояние
   * экрана, что показывает статус-бар (там его закрывает затемнение панели).
   * Один источник, второго таймера нет.
   */
  actionNotice?: ActionNotice | null;
  /** Панель открыта действием «заметки» со строки — фокус сразу в разделе. */
  focusNotes: boolean;
  /**
   * Скопировать тикер этой плотности (§9 1.44) в буфер обмена —
   * то же действие, что и у кнопки строки таблицы. Карточкам §9 (1.21) места
   * под него не досталось, и панель несёт его вместо строки (design.md change
   * stage6-density-cards, решение 8).
   */
  onCopyTicker: (row: DensitySnapshot) => void;
  /** Закрепить уровень этой плотности или снять уже стоящее закрепление. */
  onTogglePin: (row: DensitySnapshot) => void;
  /**
   * Закрепление §6.7, сопоставленное с этой плотностью — то же сопоставление,
   * что видит строка таблицы (`matchPinsToRows`). `"unknown"` — список ещё не
   * прочитан или недоступен.
   */
  pinMatch: RowPinMatch | "unknown" | undefined;
  /** Свой запрос закрепления этой плотности ещё не завершился. */
  pinPending: boolean;
}

/**
 * Содержимое детальной панели §9. Рисуется без портала — оболочка `Sheet`
 * снаружи: `@base-ui/react` уводит панель в портал, а статический рендер
 * порталов не рисует, и вложенное прямо в `SheetContent` содержимое было бы
 * тестам невидимо целиком.
 *
 * Клиент здесь ничего не считает: величины §6.2 приходят от демона готовыми.
 */
export const DensityDetailContent = memo(function DensityDetailContent({
  wrapped,
  row,
  state,
  onRefresh,
  notesState,
  onCreateNote,
  onUpdateNoteText,
  onDeleteNote,
  favoriteBases,
  onToggleFavorite,
  pendingFavoriteBases,
  favoriteNotice,
  onHideCoin,
  hideNotice = null,
  actionNotice = null,
  focusNotes,
  onCopyTicker,
  onTogglePin,
  pinMatch,
  pinPending,
}: DensityDetailContentProps) {
  const planGate = usePlanGate();
  // Последний осевший ответ по текущей плотности: повторный запрос (кнопка
  // «Обновить») не обязан размонтировать контент и ронять фокус — старые
  // величины остаются на месте, приглушённо, пока не придут новые.
  const lastReadyRef = useRef<{ densityId: string; detail: DensityDetail } | null>(null);
  if (state.status === "ready") {
    lastReadyRef.current = { densityId: state.detail.density.density_id, detail: state.detail };
  }
  const cachedReady =
    lastReadyRef.current !== null && lastReadyRef.current.densityId === row.density_id
      ? lastReadyRef.current.detail
      : null;
  // Кэш подставляется только во время загрузки той же плотности (обновление):
  // при смене плотности или при отказе он не имеет права всплыть — то и
  // другое обязано выглядеть как «нечего показать», а не как чужие величины.
  const showCachedWhileLoading = state.status === "loading" && cachedReady !== null;
  const detailToShow =
    state.status === "ready" ? state.detail : showCachedWhileLoading ? cachedReady : null;
  const refreshing = state.status === "loading" && detailToShow !== null;
  // `detailToShow === null` влечёт `state.status !== "ready"` логически, но не
  // для контроля потоков TypeScript: подстановка ниже нужна только для сужения
  // типа и никогда не рендерится, когда состояние на самом деле `ready`.
  const requestState: Exclude<DetailState, { status: "ready" }> =
    state.status === "ready" ? { status: "loading" } : state;

  // Плотность берётся из ответа, когда он есть: он собран между событиями
  // и несёт то, чего в строке таблицы нет вовсе, — подходы и контекст.
  //
  // Кроме пяти живых величин §7.6 (1.13) и цены якоря: якорь плавает внутри
  // кластера, и закреплённая цена сверяется именно с ним. Их строка получает
  // срезом не реже `live_refresh_ms`, а ответ §7.6 застыл на момент `GET` и с
  // каждой секундой панели врёт всё сильнее: рядом с живой строкой таблицы
  // стояла бы её же дистанция получасовой давности, а крупная цена якоря
  // разошлась бы со строкой «Закреплено на X». Поэтому эти шесть берутся у
  // строки, а всё остальное — у ответа.
  const density =
    detailToShow !== null
      ? {
          ...detailToShow.density,
          anchor_price: row.anchor_price,
          distance_pct: row.distance_pct,
          density_usd: row.density_usd,
          density_qty: row.density_qty,
          current_levels_count: row.current_levels_count,
          mm_flags: row.mm_flags,
        }
      : row;

  const favoriteMatch: FavoriteIndicator =
    favoriteBases === null ? "unknown" : favoriteBases.has(density.instrument.base) ? "yes" : "no";
  const favoritePending = pendingFavoriteBases.has(density.instrument.base);
  // Отказ называет монету (review stage6-favorites, п. 5): последнее
  // нажатие где-то ещё в таблице панель другой монеты не касается.
  const favoriteNoticeForCoin =
    favoriteNotice !== null && favoriteNotice.base === density.instrument.base
      ? favoriteNotice
      : null;
  const hideNoticeForCoin =
    hideNotice !== null && hideNotice.base === density.instrument.base ? hideNotice : null;

  return (
    <div
      className="flex min-h-0 flex-col"
      aria-busy={state.status === "loading" ? "true" : undefined}
    >
      <DetailHeader
        density={density}
        row={row}
        wrapped={wrapped}
        favoriteMatch={favoriteMatch}
        onToggleFavorite={() => onToggleFavorite(row)}
        favoritePending={favoritePending}
        favoriteNotice={favoriteNoticeForCoin}
        onHideCoin={() => onHideCoin(density.instrument.base)}
        hideNotice={hideNoticeForCoin}
        actionNotice={noticeForDensity(actionNotice, row.density_id)}
        onCopyTicker={() => onCopyTicker(row)}
        onTogglePin={() => onTogglePin(row)}
        pinMatch={pinMatch}
        pinPending={pinPending}
      />

      <div className="min-h-0 flex-1 overflow-auto">
        {detailToShow !== null ? (
          <>
            <LiveContext
              context={detailToShow.context}
              onRefresh={onRefresh}
              refreshing={refreshing}
            />
            {planGate.mmApproachesLocked ? (
              // Подходов и истории тариф не даёт: демон их вырезал, на их месте замок.
              <LockedBanner label="Подходы и история" text={planGate.mmApproachesText} />
            ) : (
              <ApproachHistory
                history={detailToShow.history}
                ongoing={detailToShow.ongoing_approach}
              />
            )}
          </>
        ) : (
          <RequestState state={requestState} onRefresh={onRefresh} />
        )}

        {/* Заметки живут у монеты (§9), а не у ответа §7.6 по деталям: раздел
            виден и тогда, когда сами детали не пришли (web-density-detail
            «Неудача заметки не рушит панель»). */}
        <MemoNotesSection
          base={density.instrument.base}
          notesState={notesState}
          create={onCreateNote}
          updateText={onUpdateNoteText}
          remove={onDeleteNote}
          autoFocusDraft={focusNotes}
        />
      </div>
    </div>
  );
});

/**
 * Разделы панели под `memo`: срез живых величин плотности меняет заголовок,
 * а контекст, историю подходов и заметки не трогает — их данные приходят
 * ответом деталей и состоянием заметок, ссылки на которые не меняются.
 */
const MemoNotesSection = memo(NotesSection);

/** Заголовок: те же величины §9, что и в строке, без сокращения до колонок. */
function DetailHeader({
  density,
  row,
  wrapped,
  favoriteMatch,
  onToggleFavorite,
  favoritePending,
  favoriteNotice,
  onHideCoin,
  hideNotice,
  actionNotice,
  onCopyTicker,
  onTogglePin,
  pinMatch,
  pinPending,
}: {
  density: DensitySnapshot;
  /** Исход копирования или отправки в терминал, см. `DensityDetailContentProps.actionNotice`. */
  actionNotice: ActionNotice | null;
  /** Строка таблицы той же плотности: действие терминала берёт инструмент у неё, как копирование и закрепление. */
  row: DensitySnapshot;
  wrapped: readonly WrappedCoin[];
  /** Избранное монеты §6.6 — то же состояние, что у звёздочки строки таблицы. */
  favoriteMatch: FavoriteIndicator;
  onToggleFavorite: () => void;
  favoritePending: boolean;
  favoriteNotice: FavoriteToggleNotice;
  /** Скрыть монету целиком (§75 п. 2). */
  onHideCoin: () => void;
  hideNotice: HideCoinNotice;
  /** Скопировать тикер (§9 1.44) — то же действие, что у кнопки строки. */
  onCopyTicker: () => void;
  /** Закрепить уровень этой плотности или снять уже стоящее закрепление. */
  onTogglePin: () => void;
  /** Закрепление §6.7, сопоставленное с этой плотностью — то же, что видит строка. */
  pinMatch: RowPinMatch | "unknown" | undefined;
  pinPending: boolean;
}) {
  const flagged = density.mm_flags.length > 0;
  const pinState = rowPinState(row, pinMatch);
  const planGate = usePlanGate();

  return (
    <div className="flex flex-col gap-1.5 border-b border-border px-3 py-2.5 pr-12">
      <div className="flex items-center gap-2">
        <SideBadge side={density.side} flagged={flagged} />
        <span className="font-medium text-foreground">
          {tickerLabel(density.instrument, density.exchange_base)}
        </span>
        <WrappedMark base={density.instrument.base} wrapped={wrapped} />
        <span className="text-xs text-muted-foreground">{venueOf(density.instrument)}</span>
        {/* Действия строки, которым в карточке §9 (1.21) не досталось места:
            копировать тикер и закрепить уровень (design.md решение 8). */}
        <span className="ml-auto flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={`Скопировать тикер ${tickerForClipboard(density)}`}
            className="size-6 text-muted-foreground"
            onClick={onCopyTicker}
          >
            <RiFileCopyLine className="size-3.5" />
          </Button>
          <TerminalDetailAction row={row} />
          <PinToggleButton pinMatch={pinMatch} pending={pinPending} onToggle={onTogglePin} />
          <FavoriteStarButton
            match={favoriteMatch}
            pending={favoritePending}
            onToggle={onToggleFavorite}
          />
          {/* Скрытие — отдельная кнопка со словами: действие необратимо из
              самой панели (вернуть монету можно только из листа «Скрытые
              монеты»), и значок без подписи тут был бы слишком тихим. */}
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="xs"
                  data-slot="detail-hide-coin"
                  className="text-muted-foreground"
                  onClick={onHideCoin}
                />
              }
            >
              <RiEyeOffLine aria-hidden="true" />
              Скрыть монету
            </TooltipTrigger>
            <TooltipContent>
              {`Скрыть монету ${density.instrument.base} на всех биржах и рынках`}
            </TooltipContent>
          </Tooltip>
        </span>
      </div>

      {/* Открыть в терминале (§10 1.45): путь к настройкам всегда, а когда
          действия нет — перед ним причина словами. */}
      <TerminalDetailSettings row={row} />

      {/* Исход копирования и отправки в терминал: статус-бар экрана закрыт
          затемнением панели, поэтому панель называет исход сама. Регион
          смонтирован всегда, меняется только текст — иначе `aria-live`
          объявляет вставленное ненадёжно. */}
      <p
        data-slot="detail-action-notice"
        aria-live="polite"
        className={cn(
          "text-xs empty:hidden",
          actionNotice?.outcome === "failed" ? "text-destructive" : "text-muted-foreground",
        )}
      >
        {actionNotice?.text ?? ""}
      </p>

      {/* Отказ записи избранного §6.6 из панели (web-density-detail «Отказ
          записи из панели»): назван здесь, а не только в общей полосе над
          списком — та скрыта затемнением `Sheet`, пока панель открыта.
          Уже отфильтрован по монете вызывающей стороной (review
          stage6-favorites, п. 5). */}
      {favoriteNotice !== null ? (
        <p role="alert" className="text-xs text-destructive">
          {favoriteToggleNoticeText(favoriteNotice)}
        </p>
      ) : null}

      {/* Отказ скрытия монеты (web-density-detail «Неудача скрытия из
          панели»): панель остаётся открытой и называет отказ сама. */}
      {hideNotice !== null ? (
        <p role="alert" className="text-xs text-destructive">
          {hideCoinNoticeText(hideNotice)}
        </p>
      ) : null}

      <div className="flex items-baseline gap-3 tabular-nums">
        <span className="text-lg font-medium text-foreground">
          {formatPrice(density.anchor_price)}
        </span>
        <span className="text-sm text-muted-foreground">
          {formatDistance(density.distance_pct)}
        </span>
        <span className="text-sm text-foreground">{formatUsd(density.density_usd)}</span>
        <span className="text-xs text-muted-foreground">{formatQty(density.density_qty)}</span>
        {pinState.match !== null ? (
          <span data-slot="detail-pin-state" className="text-xs text-muted-foreground">
            {`Закреплено на ${formatPrice(pinState.match.pin.price)}`}
            {pinState.driftText !== null ? (
              <span
                data-slot="detail-pin-drift"
                className="text-pin"
              >{` ${pinState.driftText}`}</span>
            ) : null}
          </span>
        ) : null}
      </div>

      <dl className="grid grid-cols-2 gap-x-4 text-xs tabular-nums sm:grid-cols-4">
        <Field label="Состояние" value={stateLabel(density.state)} />
        <Field label="Уровней" value={String(density.current_levels_count)} />
        <Field
          label="Подходов"
          value={
            planGate.mmApproachesLocked ? (
              <LockedInline label="Подходы" text={planGate.mmApproachesText} />
            ) : (
              String(density.approaches_count)
            )
          }
        />
        <AgeField label="Живёт" sinceMs={density.detected_at_ms} />
      </dl>

      {/* Флаги ММ — пометка §4.5, а не причина не показывать плотность:
          скрытие помеченных делает фильтр таблицы, и панель о нём не знает. */}
      {planGate.mmApproachesLocked ? (
        <div className="flex items-center gap-1">
          <span className="text-xs text-muted-foreground">Признаки ММ:</span>
          <LockedInline label="Признаки ММ" text={planGate.mmApproachesText} />
        </div>
      ) : flagged ? (
        <div className="flex items-center gap-1">
          <span className="text-xs text-muted-foreground">Признаки ММ:</span>
          <MmFlagBadges flags={density.mm_flags} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Живой контекст §6.2 по стакану. Ликва и фронтран здесь ничего не сортируют
 * и не фильтруют (§9 1.16): это величины одного взгляда, а не колонка таблицы.
 */
const LiveContext = memo(function LiveContext({
  context,
  onRefresh,
  refreshing,
}: {
  context: DensityContextView;
  onRefresh: () => void;
  /** Идёт повторный запрос: прежние величины остаются на экране приглушённо. */
  refreshing: boolean;
}) {
  return (
    <section className={cn("border-b border-border px-3 py-2.5", refreshing && "opacity-60")}>
      <div className="flex items-center gap-2">
        <SectionLabel as="h3">Контекст по стакану</SectionLabel>
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">
          {context.kind === "measured" ? (
            <>
              {formatClock(context.measured_at_ms)} · <Ago tsMs={context.measured_at_ms} /> назад
            </>
          ) : null}
        </span>
        {/* Величина не обновляется сама: она снята по запросу, и момент
            расчёта стоит рядом именно поэтому. Кнопка остаётся смонтированной
            весь запрос — `aria-busy` на корне уже говорит о состоянии,
            размонтирование сюда добавило бы только потерю фокуса. */}
        <Button
          variant="outline"
          size="sm"
          className="h-6 px-2 text-xs"
          aria-disabled={refreshing ? "true" : undefined}
          onClick={() => {
            if (!refreshing) {
              onRefresh();
            }
          }}
        >
          Обновить
        </Button>
      </div>

      {context.kind === "book_not_synced" ? (
        <p className="mt-3 text-sm text-muted-foreground">
          Стакан не синхронизирован — величины по нему не считались. Числа появятся, когда
          инструмент досинхронизируется.
        </p>
      ) : (
        <>
          <dl className="mt-1.5 grid grid-cols-2 gap-x-4 text-xs tabular-nums sm:grid-cols-3">
            <Field label="Ликва всего" value={formatUsd(context.liq_behind_total_usd)} />
            <Field label="Ликва чистая" value={formatUsd(context.liq_behind_clean_usd)} />
            <Field label="Ликва медиана" value={formatUsd(context.liq_behind_median_usd)} />
            <Field label="Фронтран" value={formatUsd(context.frontrun_usd)} />
            <Field label="Фронтран уровней" value={String(context.frontrun_count)} />
            <Field
              label="Фронтран ближайший"
              value={formatFrontrunNearest(context.frontrun_nearest_pct, context.frontrun_count)}
            />
          </dl>
          <p className="mt-1.5 text-xs text-muted-foreground">
            Покрытие зон: {formatPercent(context.coverage_pct, 0)} подтверждённой глубины.
          </p>
        </>
      )}
    </section>
  );
});

/**
 * История стены (§9, §60 п. 3): записи библиотеки по возрастанию подтверждения
 * (порядок сервера), у каждой — исход, счётчики, флаги ММ и закрытые подходы
 * по возрастанию номера. Идущий подход приходит от трекера поиска, а не из
 * библиотеки, и потому виден отдельно и даже когда историю прочитать не удалось.
 */
const ApproachHistory = memo(function ApproachHistory({
  history,
  ongoing,
}: {
  history: DensityDetail["history"];
  ongoing: OngoingApproachView | null;
}) {
  return (
    <section className="px-3 py-2.5">
      <SectionLabel as="h3">Подходы</SectionLabel>

      {history.kind === "unavailable" ? (
        <p className="mt-3 text-sm text-muted-foreground">{LIBRARY_HISTORY_LABELS.unavailable}</p>
      ) : history.records.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">{LIBRARY_HISTORY_LABELS.noRecords}</p>
      ) : (
        <ul className="mt-1.5 flex flex-col gap-2">
          {history.records.map((record) => (
            <li key={record.density_id}>
              <LibraryRecord record={record} />
            </li>
          ))}
        </ul>
      )}

      {ongoing ? (
        <div className="mt-3">
          <OngoingApproach approach={ongoing} />
        </div>
      ) : null}
    </section>
  );
});

function LibraryRecord({ record }: { record: LibraryRecordView }) {
  const approaches = [...record.approaches].sort(
    (left, right) => left.approach_index - right.approach_index,
  );

  return (
    <div className="rounded-md border border-border px-2.5 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-foreground">
          {record.outcome === null
            ? LIBRARY_HISTORY_LABELS.recordOngoing
            : stateLabel(record.outcome)}
        </span>
        <MmFlagBadges flags={record.mm_flags} />
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">
          {formatDateTime(record.confirmed_at_ms)} →{" "}
          {record.ended_at_ms === null ? "…" : formatDateTime(record.ended_at_ms)}
        </span>
      </div>
      <dl className="mt-1 grid grid-cols-2 gap-x-4 text-xs tabular-nums sm:grid-cols-4">
        <Field label="Пик" value={formatUsd(record.peak_usd)} />
        <Field label="Подходов" value={String(record.approaches_count)} />
        <Field label="Перекотировок" value={String(record.requote_count)} />
        <Field label="Сдвигов стены" value={String(record.moved_count)} />
      </dl>
      {approaches.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">{LIBRARY_HISTORY_LABELS.noApproaches}</p>
      ) : (
        <ul className="mt-2 flex flex-col gap-1.5">
          {approaches.map((approach) => (
            <li key={approach.approach_index}>
              <ClosedApproach approach={approach} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Исходы, при которых подход не был досмотрен до конца: `Lost` — плотность
 * перестала быть видна демону, `Stopped` — демон остановлен. Ни тот, ни
 * другой не говорят, устояла стена или нет, и по ним нельзя судить о ней
 * наравне с `Held` или `Eaten`.
 */
const UNWATCHED_OUTCOMES: ApproachOutcomeView[] = ["lost", "stopped"];

const OUTCOME_LABELS: Record<ApproachOutcomeView, string> = {
  held: "Held",
  eaten: "Eaten",
  pulled: "Pulled",
  timeout: "Timeout",
  lost: "Lost",
  stopped: "Stopped",
};

function ClosedApproach({ approach }: { approach: ClosedApproachView }) {
  const unwatched = UNWATCHED_OUTCOMES.includes(approach.outcome);

  return (
    <div className="rounded-md border border-border px-2.5 py-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground tabular-nums">
          #{approach.approach_index}
        </span>
        <span className="text-sm font-medium text-foreground">
          {OUTCOME_LABELS[approach.outcome]}
        </span>
        {unwatched ? (
          <Tooltip>
            <TooltipTrigger render={<span className="text-[11px] text-warning" />}>
              недосмотрен
            </TooltipTrigger>
            <TooltipContent>
              Подход не был досмотрен до конца: исход ничего не говорит об устойчивости стены.
            </TooltipContent>
          </Tooltip>
        ) : null}
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">
          {formatClock(approach.started_at_ms)} → {formatClock(approach.ended_at_ms)}
        </span>
      </div>
      <ApproachMetrics metrics={approach.metrics} />
    </div>
  );
}

function OngoingApproach({ approach }: { approach: OngoingApproachView }) {
  return (
    <div className="rounded-md border border-border bg-muted/40 px-2.5 py-2">
      <div className="flex items-center gap-2">
        {/* Номера может не быть вовсе: заход к стене, не дошедшей до
            `Confirmed`, номера не занимает (§6.2). */}
        {approach.approach_index === null ? null : (
          <span className="text-xs font-medium text-muted-foreground tabular-nums">
            #{approach.approach_index}
          </span>
        )}
        {/* Исхода нет, а не «нулевой»: подход ещё идёт, и написать сюда
            `Held` значило бы соврать об уже случившемся. */}
        <span className="text-sm font-medium text-foreground">Идёт</span>
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">
          {formatClock(approach.started_at_ms)} · <Ago tsMs={approach.started_at_ms} /> назад
        </span>
      </div>
      <ApproachMetrics metrics={approach.metrics} />
    </div>
  );
}

/**
 * Все величины §6.2 подхода — те же у закрытого и идущего, сгруппированные по
 * смыслу. Закрытый и идущий подход несут один состав с живым контекстом
 * (openspec/specs/web-density-detail): величина, посчитанная по неполному
 * покрытию, — не то же самое, что по полному, и это обязано быть видно.
 */
function ApproachMetrics({ metrics }: { metrics: ApproachMetricsView }) {
  return (
    <div className="mt-1.5 flex flex-col gap-2">
      <MetricsGroup title="Подход">
        <Field label="Дисконт" value={formatPercent(metrics.min_distance_pct)} />
        <Field label="Отскок" value={formatPercent(metrics.max_bounce_pct)} />
        <Field label="Длительность отскока" value={formatAge(metrics.bounce_duration_sec)} />
        <Field label="В зоне" value={formatAge(metrics.time_at_density_sec)} />
        <Field label="Длительность съедения" value={formatOptionalAge(metrics.eat_duration_sec)} />
        <Field label="Цена входа" value={formatPrice(metrics.price_at_start)} />
        <Field label="Реализовано" value={formatUsd(metrics.realized_usd)} />
        <Field label="Доля" value={formatPercent(metrics.realized_share * 100, 0)} />
        {/* Не «съедено»: §6.2 (ревизия 1.11) убрала это слово — оно читалось
            и как исход `Eaten`, и как любой из зачитываемых исходов сразу. */}
        <Field label="Поглощено соседом" value={formatUsd(metrics.absorbed_before_usd)} />
        <Field
          label="До следующей плотности"
          value={formatOptionalPercent(metrics.next_density_distance_pct)}
        />
        <Field label="Покрытие зон" value={formatPercent(metrics.coverage_pct, 0)} />
      </MetricsGroup>

      <MetricsGroup title="Фронтран">
        <Field label="Фронтран" value={formatUsd(metrics.frontrun_usd)} />
        <Field label="Фронтран уровней" value={String(metrics.frontrun_count)} />
        <Field
          label="Фронтран ближайший"
          value={formatFrontrunNearest(metrics.frontrun_nearest_pct, metrics.frontrun_count)}
        />
        <Field label="Фронтран реализовано" value={formatUsd(metrics.frontrun_realized_usd)} />
      </MetricsGroup>

      <MetricsGroup title="Ликва за плотностью">
        <Field label="Ликва всего" value={formatUsd(metrics.liq_behind_total_usd)} />
        <Field label="Ликва чистая" value={formatUsd(metrics.liq_behind_clean_usd)} />
        <Field label="Ликва медиана" value={formatUsd(metrics.liq_behind_median_usd)} />
        <Field label="Ликва на тесте" value={formatOptionalUsd(metrics.liq_behind_at_test_usd)} />
      </MetricsGroup>

      <MetricsGroup title="Открытый интерес">
        <Field label="OI на входе" value={formatOptionalUsd(metrics.oi_at_start)} />
        <Field label="OI на выходе" value={formatOptionalUsd(metrics.oi_at_end)} />
      </MetricsGroup>
    </div>
  );
}

function MetricsGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h4 className="text-[10px] tracking-wider text-muted-foreground/80 uppercase">{title}</h4>
      {/* Число колонок по ширине панели, а не постоянная тройка: панель
          широкая, и тройка оставляла половину её пустой. */}
      <dl className="mt-0.5 grid grid-cols-2 gap-x-4 text-xs tabular-nums sm:grid-cols-3 lg:grid-cols-4">
        {children}
      </dl>
    </div>
  );
}

/**
 * Величина панели: метка и значение в одну строку, значение прижато вправо.
 *
 * В столбик каждая величина занимала две строки, и панель §9 на широком
 * экране была наполовину пустой (замечание владельца по первому боевому
 * подъёму, 22.09.2026). Прижатое вправо значение держит числа одной
 * колонкой — при `tabular-nums` их можно сравнивать глазом, чего столбик
 * с разной длиной меток не давал.
 */
const Field = memo(function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2 border-b border-border/40 py-0.5">
      <dt className="text-[11px] text-muted-foreground">{label}</dt>
      <dd className="text-foreground">{value}</dd>
    </div>
  );
});

/** Величина-возраст: значение — лист времени, поэтому сама величина зависит только от момента. */
const AgeField = memo(function AgeField({ label, sinceMs }: { label: string; sinceMs: number }) {
  return <Field label={label} value={<Elapsed sinceMs={sinceMs} />} />;
});

/** Панель без деталей: сама плотность из потока уже показана заголовком. */
function RequestState({
  state,
  onRefresh,
}: {
  state: Exclude<DetailState, { status: "ready" }>;
  onRefresh: () => void;
}) {
  if (state.status === "loading") {
    return (
      <LoadingRegion label="Загружаем детали…" busy={false} className="flex flex-col gap-6 p-4">
        {Array.from({ length: 3 }, (_, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: статичные декоративные разделы
          <div key={index} className="flex flex-col gap-3">
            <Skeleton className="h-3 w-28" />
            <SkeletonLines count={3} />
          </div>
        ))}
      </LoadingRegion>
    );
  }
  if (state.status === "not-found") {
    return (
      <p className="p-4 text-sm text-muted-foreground">
        Плотности уже нет: демон её не знает. Исход остался в ленте недавних исходов.
      </p>
    );
  }
  return (
    <div className="flex flex-col items-start gap-3 p-4">
      <p className="text-sm text-muted-foreground">
        Демон недоступен — детали не пришли. Сама плотность выше взята из потока.
      </p>
      <Button variant="outline" size="sm" onClick={onRefresh}>
        Повторить
      </Button>
    </div>
  );
}
